import { Injectable } from '@angular/core';
import { LogService } from './log.service';
import { ExperimentResult, ExperimentationSDK } from '../models/experimentation.model';
import { properties } from 'src/environments/properties';

/**
 * Encapsulates all AB testing / experimentation logic.
 *
 * The experimentation SDK (`experimentation.bundle.js`) is loaded by the AEM host
 * page and exposed at `window['AB'].experimentation`. This service provides a
 * safe, testable wrapper around the SDK so components never access `window` directly.
 *
 * ## Usage
 *
 * ```typescript
 * constructor(private experimentService: ExperimentationService) {}
 *
 * async ngOnInit() {
 *   const result = await this.experimentService.getExperiment('EXP-12345');
 *   if (result.isEligible && result.variant === 'variant-1') {
 *     this.showNewLayout = true;
 *   }
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class ExperimentationService {

  /** Overridden in tests — never read `window` directly */
  public _window: any = window;

  /** In-memory cache: experimentId → resolved result */
  private _cache = new Map<string, ExperimentResult>();

  constructor(private logService: LogService) {}

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Whether the experimentation SDK is available on the page.
   * Returns `false` in local dev or test environments where AEM has not
   * injected `experimentation.bundle.js`.
   */
  isSDKAvailable(): boolean {
    return typeof this._window?.['AB']?.experimentation?.getExperimentByID === 'function';
  }

  /**
   * Retrieve the experiment assignment for the current user.
   *
   * - Returns cached result on subsequent calls for the same `experimentId`.
   * - Falls back to the BAU (control) result when the SDK is not loaded,
   *   the experiment is not found, or any error occurs.
   *
   */
  async getExperiment(): Promise<ExperimentResult> {
    const experimentId = properties.discoveryXERFeatureID;
    if (!experimentId) {
      return this._controlResult(experimentId);
    }

    // Return cached result if available
    const cached = this._cache.get(experimentId);
    if (cached) {
      return cached;
    }

    if (!this.isSDKAvailable()) {
      this.logService.log(
        `ExperimentationService: SDK not available — returning control for "${experimentId}"`
      );
      return this._controlResult(experimentId);
    }

    try {
      const sdk: ExperimentationSDK = this._window['AB'].experimentation;

      const response: any = await sdk.getExperimentByID(experimentId, false);

      // Response shape: { features: [{ featureId, experimentId, eligible, group, xerPlacement }] }
      const featureList: any[] = Array.isArray(response?.features) ? response.features : [];
      const feature = featureList.find((f: any) => f?.featureId === experimentId)
        ?? null;

      // If not found or not eligible, return control
      if (!feature) {
        this.logService.log(
          `ExperimentationService: Experiment "${experimentId}" not found in response`
        );
        return this._controlResult(experimentId);
      }

      const normalised: ExperimentResult = {
        experimentId: feature.experimentId ?? experimentId,
        variant: feature.group ?? 'A',
        isEligible: feature.eligible ?? false,
        metadata: feature.xerPlacement
      };

      this._cache.set(experimentId, normalised);

      return normalised;
    } catch (error) {
      this.logService.log(
        `ExperimentationService: Error fetching experiment "${experimentId}" — falling back to control`
      );
      return this._controlResult(experimentId);
    }
  }

  /**
   * Convenience: returns `true` if the user is eligible **and** assigned to
   * the given variant for the specified experiment.
   *
   * ```typescript
   * if (await this.experimentService.isVariant('EXP-123', 'variant-1')) { ... }
   * ```
   */
  async isVariant(experimentId: string, variant: string): Promise<boolean> {
    const result = await this.getExperiment();
    return result.isEligible && result.variant === variant;
  }

  /**
   * Clear the in-memory cache. Useful when the user session changes (e.g.,
   * after login/logout) and experiment assignments may differ.
   */
  clearCache(): void {
    this._cache.clear();
  }

  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------

  /**
   * Build a default "control" / BAU result for when the SDK is absent or errors.
   */
  private _controlResult(experimentId: string): ExperimentResult {
    const result: ExperimentResult = {
      experimentId: experimentId || '',
      variant: 'A',
      isEligible: false
    };
    this._cache.set(experimentId, result);
    return result;
  }
}
